import { retry, type RetryDelay, type ShouldRetry } from "./retry";
import { hmrValue } from "./vite";

// hands the entry over to a fetch, whose signal now governs the request
type Preloaded = (signal?: AbortSignal) => Promise<Response>;

const data: {
  originalFetch?: typeof fetch;
  cache: Map<string, Preloaded>;
} = hmrValue(
  "_CUSTOM_FETCH_",
  () => ({ cache: new Map<string, Preloaded>() }),
  import.meta.hot
);

export function installGlobalFetch() {
  if (typeof globalThis.fetch === "undefined") return;
  const props = Object.getOwnPropertyDescriptor(globalThis, "fetch");
  const originalFetch = data.originalFetch ?? props.value ?? globalThis.fetch;
  data.originalFetch = originalFetch;

  function fetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
    const cacheKey = fetchCacheKey(input, init);
    if (!cacheKey) return originalFetch(input, init);
    const cached = data.cache.get(cacheKey);
    if (!cached) return originalFetch(input, init);

    data.cache.delete(cacheKey);
    return cached(
      init?.signal ?? (input instanceof Request ? input.signal : undefined)
    );
  }

  Object.defineProperty(globalThis, "fetch", {
    ...props,
    value: fetch,
  });
}

const bytesPrefix = "bytes=";
function fetchCacheKey(
  input: RequestInfo | URL,
  init?: RequestInit
): string | null {
  const request =
    input instanceof Request && !init ? input : new Request(input, init);

  if (request.method !== "GET") {
    return null;
  }

  const range = request.headers.get("Range");
  if (!range) return request.url;
  if (!range.startsWith(bytesPrefix)) return request.url;

  const rangeValue = range.slice(bytesPrefix.length);
  return `${request.url}@${rangeValue}`;
}

// aborting the signal cancels the request and drops it from the cache,
// unless a fetch already claimed it
export function preloadFetch(
  input: string | URL | Request,
  response?: Response | ((response: Response) => Response | Promise<Response>),
  signal?: AbortSignal
): void {
  if (signal?.aborted) return;

  const cacheKey = fetchCacheKey(input);
  if (!cacheKey)
    throw new Error(
      `Cannot preload request ${input} because it is not a GET request`
    );
  // the request gets its own signal so a claimed response outlives the caller's abort
  const request = new AbortController();
  let result =
    response instanceof Response
      ? Promise.resolve(response)
      : fetch(input, { signal: request.signal });

  if (typeof response === "function") {
    result = result.then(response);
  }

  // only unclaimed entries still have this listener
  const onAbort = () => {
    if (data.cache.get(cacheKey) === entry) data.cache.delete(cacheKey);
    request.abort(signal?.reason);
    // nobody is left to observe the AbortError
    result.catch(() => void 0);
  };
  const entry: Preloaded = (claimer) => {
    signal?.removeEventListener("abort", onAbort);
    if (!claimer) return result;

    // same outcome as a fetch made with an already aborted signal
    if (claimer.aborted) {
      request.abort(claimer.reason);
      result.catch(() => void 0);
      return Promise.reject(claimer.reason);
    }

    const onClaimerAbort = () => request.abort(claimer.reason);
    claimer.addEventListener("abort", onClaimerAbort, { once: true });
    const cleanup = () => claimer.removeEventListener("abort", onClaimerAbort);
    result.then(cleanup, cleanup);
    return result;
  };
  data.cache.set(cacheKey, entry);
  signal?.addEventListener("abort", onAbort, { once: true });
}

export type RetryOptions = {
  ignoreDefaults?: boolean;
  maxAttempts?: number;
  delay?: RetryDelay<Response>;
  shouldRetry?: ShouldRetry<Response>;
};

function baseShouldRetry(
  attempt: number,
  error: unknown,
  response: Response | undefined,
  options?: RetryOptions
): boolean | Promise<boolean> {
  if (options?.ignoreDefaults) {
    return options?.shouldRetry?.(attempt, error, response) ?? false;
  }

  if (error != null) {
    return true;
  }

  const status = response?.status ?? 0;
  if (status >= 500) {
    return true;
  }

  if (status === 408) {
    // > ... the server did not receive a complete request message within the time that it was prepared to wait.
    // > If the client has an outstanding request in transit, it MAY repeat that request.
    // see: https://www.rfc-editor.org/rfc/rfc9110.html#section-15.5.9
    return true;
  }

  return options?.shouldRetry?.(attempt, error, response) ?? false;
}

export async function shouldRetryJsonFailure(
  _attempt: number,
  _error: unknown,
  response: Response | undefined
): Promise<boolean> {
  if (!response?.ok) {
    return false;
  }

  try {
    await response.clone().json();
  } catch {
    return true;
  }
  return false;
}

export default async function fetchRetry(
  input: RequestInfo,
  init?: RequestInit & RetryOptions
): Promise<Response> {
  const shouldRetry: ShouldRetry<Response> = (attempt, error, response) =>
    baseShouldRetry(attempt, error, response, init);

  return retry(
    () => {
      const _input =
        typeof Request !== "undefined" && input instanceof Request
          ? (input.clone() as RequestInfo)
          : input;
      return fetch(_input, init);
    },
    {
      maxAttempts: init?.maxAttempts ?? 4,
      delay: init?.delay ?? "backoff",
      shouldRetry,
    }
  );
}
